import React from 'react';
import { useQuery } from '@apollo/client';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { writeFile, utils } from 'xlsx';
import LoadingSpinner from '../LoadingSpinner';
import { StyledLink } from '../Components';
import { Modal } from '../dialogs/useModal';
import { useConfig } from '../env';
import { GET_GUIDE_REFERENCED_BY } from './guideReferencedBy.graphql';

export interface GuideReferencedByDialogProps {
  guideId: string;
  docName: string | null | undefined;
  docTitle: string | null | undefined;
}

function formatLocation(headingNumber: string | null | undefined, headingText: string | null | undefined): string {
  const parts: string[] = [];
  if (headingNumber) parts.push(`pkt. ${headingNumber}`);
  if (headingText) parts.push(headingText);
  return parts.join(' ');
}

function exportToExcel(rows: { docName?: string | null; docTitle?: string | null; headingNumber?: string | null; headingText?: string | null }[], docName: string | null | undefined) {
  const headings = ['Anvisning', 'Tittel', 'Sted'];
  const data = rows.map((row) => [row.docName ?? '', row.docTitle ?? '', formatLocation(row.headingNumber, row.headingText)]);
  data.unshift(headings);
  const workbook = utils.book_new();
  const sheet = utils.aoa_to_sheet(data);
  utils.book_append_sheet(workbook, sheet, 'Henviser til');
  writeFile(workbook, `anvisninger som henviser til ${docName ?? ''}.xlsx`);
}

export function GuideReferencedByDialog({ modal }: { modal: Modal<GuideReferencedByDialogProps, unknown> }) {
  const { guideId, docName, docTitle } = modal.data;
  const { data, loading } = useQuery(GET_GUIDE_REFERENCED_BY, { variables: { id: guideId } });
  const config = useConfig();

  const rows = (data?.guideReferencedBy ?? []).flatMap((ref) =>
    (ref.locations ?? []).map((loc, idx) => ({
      key: `${ref.guideId}-${idx}`,
      guideId: ref.guideId,
      docId: ref.docId,
      docName: ref.docName,
      docTitle: ref.docTitle,
      headingText: loc.headingText,
      headingNumber: loc.headingNumber,
      headingId: loc.headingId,
    })),
  );

  return (
    <Dialog open={true} onClose={() => modal.close()} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div" noWrap style={{ maxWidth: 900 }}>
          {`Anvisninger som henviser til ${docName ?? ''} - ${docTitle ?? ''}`}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && <LoadingSpinner />}
        {!loading && rows.length === 0 && <Typography>Ingen andre anvisninger henviser til denne anvisningen.</Typography>}
        {!loading && rows.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Anvisning</TableCell>
                <TableCell>Tittel</TableCell>
                <TableCell>Sted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell>
                    <StyledLink to={`/guide/${row.guideId}`} target="_blank" rel="noopener noreferrer">
                      {row.docName}
                    </StyledLink>
                  </TableCell>
                  <TableCell>{row.docTitle}</TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <span>
                        {row.headingNumber || row.headingText ? (
                          <>
                            {row.headingNumber && <>pkt. {row.headingNumber}</>}
                            {row.headingNumber && row.headingText ? ' ' : ''}
                            {row.headingText}
                          </>
                        ) : (
                          <span style={{ fontStyle: 'italic' }}>Uten overskrift</span>
                        )}
                      </span>
                      {row.headingNumber && config?.portalUrl && (
                        <Tooltip title="Åpne i byggforsk.no">
                          <IconButton
                            size="small"
                            href={`${config.portalUrl}/dokument/${row.docId}/#i${row.headingNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            component="a">
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <IconButton
          onClick={() => exportToExcel(rows, docName)}
          title="Eksportér til Excel"
          size="large"
          disabled={rows.length === 0}>
          <img src="/icons8-microsoft-excel.svg" alt="Excel icon" />
        </IconButton>
        <Button onClick={() => modal.close()} variant="contained">
          Lukk
        </Button>
      </DialogActions>
    </Dialog>
  );
}
